import React, { Component, Fragment } from 'react'
import { Text } from 'app/common/typography'
import { breakpoints, media, style } from 'app/assets/styles'
import { colors } from 'shared/assets/styles'
import styled from 'styled-components'
import { injectIntl, FormattedMessage } from 'react-intl'
import type { IntlShape } from 'react-intl'
import { OrderStatusList } from 'app/ecommerce/common/components/order-status-list'
import type { CacOrder, DeviceCheck, OrbOrderShortDTO } from 'shared/types'
import { MenuItem } from 'app/common/layout/menu'
import { Close } from 'app/assets/icons'
import Modal from 'react-modal'
import { OrderDetails } from 'app/ecommerce/common/components/order-details/OrderDetails'
import { SplitLayout } from 'app/common/layout/split'
import type { EventMetadata } from 'shared/store/metadata'
import { Loader } from 'app/common/loader'
import type { FetchOrdersParams } from 'shared/store/ecommerce/order'

const { grey, text } = colors
const { medium } = breakpoints
const { classicModalMobileStyle, classicModalStyle } = style

type Props = {
    isParkingOpen: () => void,
    sendMeta: EventMetadata => void,
    discard: (order: CacOrder) => void,
    cancelOrder: (
        id: string,
        token: string,
        deviceCheckPayload: DeviceCheck
    ) => void,
    fetchOrders: (fetchOrdersParams?: FetchOrdersParams) => void,
    pastOrders: Array<CacOrder | OrbOrderShortDTO>,
    currentOrders: Array<CacOrder | OrbOrderShortDTO>,
    intl: IntlShape
}

type State = {
    activeLeft: boolean,
    currentOrder?: CacOrder,
    displayAllOrdersLink: boolean,
    loadingAllOrders: boolean,
    width: number
}

class OrdersList extends Component<Props, State> {
    state = {
        activeLeft: false,
        currentOrder: undefined,
        displayAllOrdersLink: true,
        loadingAllOrders: false,
        width: window.innerWidth
    }

    componentDidMount() {
        const { fetchOrders, isParkingOpen } = this.props
        fetchOrders()
        isParkingOpen()
        window.addEventListener('resize', this.updateWindowDimensions)
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions)
    }

    updateWindowDimensions = () => this.setState({ width: window.innerWidth })

    renderDetailModal = () => {
        const { width, currentOrder } = this.state
        return (
            <Modal
                isOpen={!!currentOrder}
                style={
                    width < medium ? classicModalMobileStyle : classicModalStyle
                }
                centered>
                <CloseButtonContainer
                    onClick={() =>
                        this.setState({
                            currentOrder: undefined
                        })
                    }>
                    <Close color={text} size={24} />
                </CloseButtonContainer>
                <OrderDetails order={currentOrder} />
            </Modal>
        )
    }

    onItemClick = (order: CacOrder) => {
        this.setState({ currentOrder: order })
    }

    renderOrdersList = (
        orders: Array<CacOrder | OrbOrderShortDTO>,
        allOrdersButton?: boolean
    ) => {
        const { discard, cancelOrder, sendMeta } = this.props
        const { displayAllOrdersLink } = this.state
        return orders.length === 0 ? (
            <FormattedMessage id="clickandcollect.listorders.empty">
                {(text: string) => <Text color={grey}>{text}</Text>}
            </FormattedMessage>
        ) : (
            <Fragment>
                <OrderStatusList
                    cardLayout="big"
                    items={orders}
                    onClick={this.onItemClick}
                    discard={discard}
                    sendMeta={sendMeta}
                    cancelOrder={cancelOrder}
                />
                {allOrdersButton &&
                    displayAllOrdersLink &&
                    this.renderAllOrdersButton()}
            </Fragment>
        )
    }

    renderAllOrdersButton = () => {
        const { fetchOrders } = this.props
        const { loadingAllOrders } = this.state
        return (
            <StyledLoader
                loading={loadingAllOrders}
                loaderMessage="kingdom.orders.display.all.loading.message"
                color={text}>
                <TextContainer
                    onClick={async () => {
                        this.setState({
                            loadingAllOrders: true
                        })
                        await fetchOrders({ fetchAllOrders: true })
                        this.setState({
                            displayAllOrdersLink: false,
                            loadingAllOrders: false
                        })
                    }}>
                    <FormattedMessage id="kingdom.orders.display.all.link">
                        {(text: string) => (
                            <UnderlinedText>{text}</UnderlinedText>
                        )}
                    </FormattedMessage>
                </TextContainer>
            </StyledLoader>
        )
    }

    render() {
        const { pastOrders, currentOrders, intl } = this.props
        const { activeLeft } = this.state
        const routeDashboard = intl.formatMessage({
            id: 'routes.kingdom.dashboard'
        })
        const breadcrumb = [
            {
                link: routeDashboard,
                title: intl.formatMessage({
                    id: 'kingdom.dashboard.title.breadcrumb.end'
                })
            },
            {
                title: intl.formatMessage({
                    id: 'kingdom.orders.title.breadcrumb'
                })
            }
        ]

        return (
            <Container>
                {this.renderDetailModal()}
                <StyledMenuItem
                    breadcrumb={breadcrumb}
                    url={routeDashboard}
                    title={intl.formatMessage({
                        id: 'clickandcollect.listorders.title'
                    })}>
                    <MainContainer>
                        <SplitLayout
                            activeLeft={activeLeft}
                            leftBlock={this.renderOrdersList(pastOrders, true)}
                            valuesTitleLeft={{ number: pastOrders.length }}
                            rightBlock={this.renderOrdersList(currentOrders)}
                            idTitleLeft="clickandcollect.listorders.past"
                            idTitleRight="clickandcollect.listorders.current"
                        />
                    </MainContainer>
                </StyledMenuItem>
            </Container>
        )
    }
}

const Container = styled.div`
    ${media.medium`
        display: flex;
        padding-top: 35px;
        flex-direction: column;
        align-self: center;
        justify-content: 
    `};
`

const StyledMenuItem = styled(MenuItem)`
    display: flex;
    flex-direction: column;
`

const MainContainer = styled.div`
    flex: 1;
    flex-direction: column;
`

const CloseButtonContainer = styled.div`
    margin: 20px;
    cursor: pointer;
`

const TextContainer = styled.div`
    display: flex;
    justify-content: center;
`

const UnderlinedText = styled(Text)`
    cursor: pointer;
    border-bottom: 1px solid;
    border-bottom-color: ${text};
`

const StyledLoader = styled(Loader)`
    margin-top: 20px;
`

const OrdersListComponent = injectIntl(OrdersList)

export { OrdersListComponent }
